import React, { useCallback, useEffect, useState } from 'react';
import { View, StyleSheet, FlatList, TouchableOpacity, Platform } from 'react-native';
import { Text } from '@/components/common/text';
import { StackScreenProps } from '@react-navigation/stack';
import { useFocusEffect } from '@react-navigation/native';
import { useDispatch } from 'react-redux';
import { 
  BaseAdminScreen, 
  AdminEmptyScreenLayout, 
  LoadingSpinner,
  BulletinDataTable,
  BulletinFeedTabs,
  BulletinRecipientTabs,
  AdminBulletinCard,
  SearchBar,
  InlineEmptyMessage,
} from '@/components';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { useBulletins } from '@/hooks/admin/useBulletins';
import { IBulletinTableRow, BulletinFeedFilter } from '@/types/admin/bulletins';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { useErrorToast } from '@/contexts/admin/ToastContext';
import { SVG_COMPONENTS } from '@/assets/admin';
import { EMPTY_MESSAGES } from '@/constants/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { getBulletinsEmptyMessage } from '@/utils/admin/bulletinUtils';
import { RecipientType } from '@/components/admin/bulletins/BulletinRecipientTabs';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopBulletinsView } from './desktop/DesktopBulletinsView';
import { DataTableProvider } from '@/contexts/admin/DataTableContext';
import { bulletinsApi } from '@/store/api/admin/bulletinsApi';

type BulletinsScreenProps = StackScreenProps<AdminStackParamList, 'Bulletins'>;

const CSV_COLUMNS = ["subject", "date", "time", "recipients", "delivered", "read"]; // Match admin-web column names

export const BulletinsScreen: React.FC<BulletinsScreenProps> = ({
  navigation,
}) => {
  const dispatch = useDispatch();
  const { layoutMode } = useResponsive();
  const { redesign, semantic, admin } = useAdminColors();
  const [csvData, setCsvData] = useState<(string | number | boolean | null)[][]>([CSV_COLUMNS]);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('cards'); // Default to cards for mobile
  const [recipientType, setRecipientType] = useState<RecipientType>('all'); // Default to 'all' like admin-web
  const [expandedBulletins, setExpandedBulletins] = useState<Set<number>>(new Set()); // Track expanded bulletins
  const [isTabSwitching, setIsTabSwitching] = useState(false); // Track tab switching loading state
  const { showApiError } = useErrorToast();

  const {
    data,
    tableRows,
    isLoading,
    isFetching,
    isFetchingMore,
    isCurrentViewSettled,
    enhancedError,
    retryCount,
    canRetry,
    manualRetry,
    paginationModel,
    updatePaginationModel,
    goToNextPage,
    goToPreviousPage,
    hasNextPage,
    hasPreviousPage,
    feedFilter,
    updateFeedFilter,
    filterModel,
    updateFilterModel,
    sortModel,
    updateSortModel,
    searchQuery,
    updateSearchQuery,
    refresh,
    } = useBulletins();

  // Refetch data when screen comes into focus (e.g., after editing a bulletin)
  useFocusEffect(
    useCallback(() => {
      // Manually invalidate all bulletins cache to force fresh fetch
      dispatch(bulletinsApi.util.invalidateTags(['BulletinsV2']));
    }, [dispatch])
  );

  // Show toast for API errors
  useEffect(() => {
    if (enhancedError) {
      showApiError(enhancedError.originalError, enhancedError.userFriendlyMessage);
    }
  }, [enhancedError, showApiError]);

  // Determine if we should show data or empty state (moved up for use in handlers)
  const hasData = data.count > 0;
  const hasSearchQuery = searchQuery.trim() !== '';

  // Reset tab switching state when new data for the current view is ready
  useEffect(() => {
    // End tab-switching state as soon as the current view settles, even if there is no data
    if (isTabSwitching && isCurrentViewSettled) {
      setIsTabSwitching(false);
    }
  }, [isTabSwitching, isCurrentViewSettled]);

  // Format CSV data (matching admin-web logic)
  const formatCsvData = useCallback((newData: IBulletinTableRow[]) => {
    if (!newData || newData.length === 0) {
      setCsvData([CSV_COLUMNS]);
      return;
    }

    const listData = newData.map((row) => [
      row.subject, // Use subject to match admin-web and CSV column header
      row.date?.toLocaleDateString() || 'No Details Found',
      row.time?.toLocaleTimeString() || 'No Details Found',
      row.messageSentCount,
      row.pushNotificationSentCount,
      row.messageReadCount,
    ]);
    setCsvData([CSV_COLUMNS, ...listData]);
  }, []);

  // Handle bulletin selection (for future bulletin details implementation)
  const selectBulletinDetails = useCallback((bulletin: IBulletinTableRow) => {
    // TODO: Navigate to bulletin details screen or show modal
    console.log('Selected bulletin:', bulletin);
    // navigation.navigate('BulletinDetails', { bulletinId: bulletin.id });
  }, []);

  // Handle bulletin edit (for draft bulletins)
  const handleEditBulletin = useCallback((bulletin: IBulletinTableRow) => {
    // Pass the full bulletin data instead of just ID (matching admin-web pattern)
    navigation.navigate('EditBulletin', { bulletin });
  }, [navigation]);

  // Handle create bulletin
  const handleCreateBulletin = useCallback(() => {
    navigation.navigate('CreateBulletin');
  }, [navigation]);

  // Enhanced handler for feed filter changes with loading state
  const handleFeedFilterChange = useCallback((newFilter: BulletinFeedFilter) => {
    if (newFilter !== feedFilter) {
      // Only show loading state if we currently have data to avoid flickering on empty tabs
      if (hasData) {
        setIsTabSwitching(true);
      }
      updateFeedFilter(newFilter);
    }
  }, [feedFilter, updateFeedFilter, hasData]);

  // Enhanced handler for recipient type changes with loading state
  const handleRecipientTypeChange = useCallback((newRecipientType: RecipientType) => {
    if (newRecipientType !== recipientType) {
      // Only show loading state if we currently have data to avoid flickering on empty tabs
      if (hasData) {
        setIsTabSwitching(true);
      }
      setRecipientType(newRecipientType);
      // Update the filter model to trigger API call with new recipient type
      updateFilterModel({
        ...filterModel,
        recipientType: newRecipientType,
      });
    }
  }, [recipientType, filterModel, updateFilterModel, hasData]);

  // Handle bulletin description expansion toggle
  const handleToggleExpanded = useCallback((bulletinId: number) => {
    setExpandedBulletins(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bulletinId)) {
        newSet.delete(bulletinId);
      } else {
        newSet.add(bulletinId);
      }
      return newSet;
    });
  }, []);

  
  // Show error state if we have a critical error and no data and no active search
  const showErrorState = enhancedError && !hasData && !isLoading && !hasSearchQuery;
  
  // Show loading state only for initial load with no data (not for tab switching to empty states)
  const showLoadingState = isLoading && !hasData && !showErrorState && !hasSearchQuery && !isTabSwitching;

  // Render error state component
  const renderErrorState = () => (
    <View style={styles.errorContainer}>
      <SVG_COMPONENTS.X_CIRCLE width={48} height={48} color={semantic.status.error} />
      <Text style={[styles.errorTitle, { color: redesign.text.primary }]}>Unable to Load Bulletins</Text>
      <Text style={[styles.errorMessage, { color: redesign.text.muted }]}>
        {enhancedError?.userFriendlyMessage || 'Something went wrong while loading the bulletins.'}
      </Text>

      {retryCount > 0 && (
        <Text style={[styles.retryInfo, { color: redesign.text.subtle }]}>
          Retry attempts: {retryCount}
        </Text>
      )}

      {canRetry && (
        <TouchableOpacity style={[styles.retryButton, { backgroundColor: admin.formInput.borderFocused }]} onPress={manualRetry}>
          <SVG_COMPONENTS.CHEVRON_RIGHT width={16} height={16} color="#FFFFFF" />
          <Text style={styles.retryButtonText}>Try Again</Text>
        </TouchableOpacity>
      )}
    </View>
  );

  // Render bulletin cards for mobile view
  const renderBulletinCard = ({ item }: { item: IBulletinTableRow }) => (
    <AdminBulletinCard
      bulletin={item}
      onEdit={handleEditBulletin}
      isExpanded={expandedBulletins.has(item.id)}
      onToggleExpanded={handleToggleExpanded}
      feedFilter={feedFilter} // Pass feed filter context for draft detection
    />
  );

  // Get dynamic empty message based on current filter (matching admin-web)
  const getEmptyMessage = () => getBulletinsEmptyMessage(feedFilter);

  // Render empty state message for when no data but tabs should remain visible
  const renderEmptyMessage = () => {
    const { title, message } = getEmptyMessage();
    return (
      <InlineEmptyMessage
        title={title}
        message={message}
        icon="MESSAGE_BLAST"
        containerStyle={styles.emptyMessageContainer}
        titleStyle={{ ...styles.emptyMessageText, color: redesign.text.primary }}
        messageStyle={{ ...styles.emptyMessageSubtext, color: redesign.text.muted }}
      />
    );
  };

  // Render data area with loading states (keeping tabs visible)
  const renderDataArea = () => {
    // Show loading spinner when switching tabs regardless of current data to avoid empty flicker
    if (isTabSwitching) {
      return (
        <View style={styles.dataLoadingContainer}>
          <LoadingSpinner 
            message="Switching tabs..." 
            size="large"
            showMessage={true}
          />
        </View>
      );
    }

    // Render data content based on view mode
    if (viewMode === 'cards') {
      if (hasData) {
        return (
          <FlatList
            data={tableRows}
            renderItem={renderBulletinCard}
            keyExtractor={(item) => `${item.id}-${item.updatedAt || item.date?.toISOString()}`}
            showsVerticalScrollIndicator={false}
            refreshing={isLoading && !isTabSwitching}
            onRefresh={refresh}
            style={styles.dataList}
          />
        );
      }
    } else {
      // Table view
      if (hasData) {
        return (
          <DataTableProvider tableKey="admin:bulletins" defaults={{ paginationModel }}>
            <BulletinDataTable
              data={data}
              isLoading={isLoading && !isTabSwitching}
              onBulletinSelect={selectBulletinDetails}
              updateCsvData={formatCsvData}
            />
          </DataTableProvider>
        );
      }
    }

    // Show empty states
    if (hasSearchQuery) {
      return (
        <View style={[styles.noResultsContainer, Platform.OS === 'web' && { pointerEvents: 'none' }]}>
          <InlineEmptyMessage
            title={EMPTY_MESSAGES.bulletins.noResults(searchQuery).title}
            message={EMPTY_MESSAGES.bulletins.noResults(searchQuery).message}
            icon={EMPTY_MESSAGES.bulletins.noResults(searchQuery).icon}
            titleStyle={{ ...styles.noResultsTitle, color: redesign.text.primary }}
            messageStyle={{ ...styles.noResultsMessage, color: redesign.text.muted }}
          />
        </View>
      );
    }

    return renderEmptyMessage();
  };

  // Render content based on view mode
  const renderContent = () => {
    // Show full-screen loading only for initial load with no data
    if (showLoadingState && !hasData && !hasSearchQuery) {
      const loadingMessage = isTabSwitching ? "Switching tabs..." : "Loading bulletins...";
      return <LoadingSpinner message={loadingMessage} />;
    }
    
    if (showErrorState) {
      return renderErrorState();
    }

    if (viewMode === 'cards') {
      return (
        <View style={styles.cardsContainer}>
          <SearchBar
            placeholder="Search bulletins..."
            value={searchQuery}
            onChangeText={updateSearchQuery}
            style={styles.searchBar}
          />
          <BulletinRecipientTabs
            activeRecipientType={recipientType}
            onRecipientTypeChange={handleRecipientTypeChange}
          />
          <BulletinFeedTabs
            activeFilter={feedFilter}
            onFilterChange={handleFeedFilterChange}
          />
          {renderDataArea()}
        </View>
      );
    }

    // Table view for larger screens
    return (
      <View style={styles.tableContainer}>
        <SearchBar
          placeholder="Search bulletins..."
          value={searchQuery}
          onChangeText={updateSearchQuery}
          style={styles.searchBar}
        />
        <BulletinRecipientTabs
          activeRecipientType={recipientType}
          onRecipientTypeChange={handleRecipientTypeChange}
        />
        <BulletinFeedTabs
          activeFilter={feedFilter}
          onFilterChange={handleFeedFilterChange}
        />
        {renderDataArea()}
      </View>
    );
  };

  return (
    layoutMode === 'desktop' ? (
      <AdminErrorBoundary context="BulletinsScreenDesktop">
        <DesktopBulletinsView
          tableRows={tableRows}
          isLoading={isLoading}
          isFetchingMore={isFetchingMore}
          isTabSwitching={isTabSwitching}
          enhancedError={enhancedError}
          retryCount={retryCount}
          canRetry={canRetry}
          manualRetry={manualRetry}
          searchQuery={searchQuery}
          onSearchChange={updateSearchQuery}
          feedFilter={feedFilter}
          onFeedFilterChange={handleFeedFilterChange}
          recipientType={recipientType}
          onRecipientTypeChange={handleRecipientTypeChange}
          hasNextPage={hasNextPage}
          onLoadMore={goToNextPage}
          refresh={refresh}
          onCreateBulletin={handleCreateBulletin}
          onEditBulletin={handleEditBulletin}
        />
      </AdminErrorBoundary>
    ) : (
    <AdminErrorBoundary context="BulletinsScreen">
      <BaseAdminScreen
        screenName="Bulletins"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <AdminEmptyScreenLayout
          title="Bulletins"
          emptyStateType="bulletins"
          onAddPress={handleCreateBulletin}
          showAddButton={true}
          buttonType="add"
          hasContent={true} // Always show content since we handle empty states internally
          disableScrollView={true} // Keep wrapper stable to avoid TextInput blur on remount
        >
          {renderContent()}
        </AdminEmptyScreenLayout>
      </BaseAdminScreen>
    </AdminErrorBoundary>
    )
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
  cardsContainer: {
    flex: 1,
  },
  tableContainer: {
    flex: 1,
    padding: 16,
  },
  searchBar: {
    marginBottom: 16,
  },
  dataLoadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
    paddingVertical: 40,
  },
  dataList: {
    flex: 1,
  },

  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  errorMessage: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 20,
    maxWidth: 300,
  },
  retryInfo: {
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 16,
    fontStyle: 'italic',
  },
  retryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },

  // Empty message styles (when tabs are visible but no data)
  emptyMessageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 24,
  },
  emptyMessageIcon: {
    marginBottom: 16,
  },
  emptyMessageText: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 8,
  },
  emptyMessageSubtext: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 300,
  },
  noResultsContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    // Use negative margin only on native, avoid on web to prevent tab overlap
    marginTop: Platform.OS === 'web' ? 0 : -120,  
    paddingHorizontal: 20,
  },
  noResultsTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  noResultsMessage: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 300,
  },
});
